import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from '../../../../../../../components/Common/UI/DataTable';
import Checkbox from '../../../../../../../components/Common/UI/Checkbox';
import { jobDetailColElectronic, jobDetailColPaper } from './columns';
import { jobDetailsElecData, jobDetailsPaperData, JobDetailsRow } from './jobDetailsData';
import { useJobDetailsProvider } from '../../../../../../context/jobDetails';
import DivertedSamplesDialog from './Modals/Divert';
import { useGlobalSearchProvider } from '../../../../../../context/globalSearch';
import {
  getSubTableColConfig,
  JobDetailColumnType,
  JobSubTableAccount,
  jobSubTableAccountsData,
  jobTypesElectronic,
  jobTypesPaper,
} from '../subTableConfig';
import { getJobById } from '../../../data/jobStatus';

function JobDetailsTab() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { selectedTable } = useJobDetailsProvider();
  const { searchApplied } = useGlobalSearchProvider();
  const [jobDetailsState, setJobDetailsState] = useState<JobDetailsRow[]>([]);
  const [jobSubTableData, setJobSubTableData] = useState<JobSubTableAccount[]>([]);
  const [selectedDivertedAccounts, setSelectedDivertedAccounts] = useState<JobSubTableAccount[]>([]);
  const isJobApproved = (getJobById(searchParams.get('job'))?.divertedDocuments?.length ?? 0) > 0;

  /** Set data on initialization depending on job-details type. */
  useEffect(() => {
    if (id === 'Electronic') {
      setJobDetailsState(jobDetailsElecData);
    } else {
      setJobDetailsState(jobDetailsPaperData);
    }
  }, [jobDetailsElecData, jobDetailsPaperData]);

  /** Set data in sub-table on init */
  useEffect(() => {
    setJobSubTableData(jobSubTableAccountsData);
  }, [jobSubTableAccountsData]);

  const handleFilterSubTable = (data: JobSubTableAccount[], types: JobDetailColumnType[]) => {
    const newData = data.filter(job => types.includes(job.jobType));

    setJobSubTableData(newData);
  };

  /** When user select table cell to which show more data, filter sub-table data depending on 'selectedTable' state */
  useEffect(() => {
    const { column, row } = selectedTable;

    if (row === 'INSERTS' && column === 'total') {
      setJobSubTableData(jobSubTableAccountsData.filter(item => item.accountName === 'inserts'));

      return;
    }

    if (column === 'percentage') {
      return;
    }

    if (!column || column === 'total') {
      // If row is 'ACCOUNTS'
      handleFilterSubTable(jobSubTableAccountsData, id === 'Electronic' ? jobTypesElectronic : jobTypesPaper);
    } else {
      handleFilterSubTable(jobSubTableAccountsData, [column as JobDetailColumnType]);
    }
  }, [selectedTable.column, selectedTable.row]);

  /** Filter current data thru global search before show */
  const filteredThruSearchData = useMemo(() => {
    const { search: appliedSearch, field } = searchApplied;

    /** If both search and field-key exist, then do simple comparison by single key. */
    if (appliedSearch && field) {
      return jobSubTableData.filter(item =>
        // String(item[field]).toLowerCase() === String(appliedSearch).toLowerCase()
        String(item[field]).toLowerCase().includes(String(appliedSearch).toLowerCase()),
      );
    }

    /** If key not specified then search in each object value */
    if (appliedSearch) {
      const searchString = String(appliedSearch).toLowerCase();

      return jobSubTableData.filter(item =>
        Object.values(item).some(
          value =>
            // Searching for only string & number values, avoiding null & undefined
            (typeof value === 'string' && value.toLowerCase().includes(searchString)) ||
            (typeof value === 'number' && String(value).includes(searchString)),
        ),
      );
    }

    return jobSubTableData;
  }, [jobSubTableData, searchApplied]);

  const handleToggleDivert = (account: JobSubTableAccount, checked: boolean) => {
    setSelectedDivertedAccounts(currentAccounts => {
      if (checked) {
        if (currentAccounts.some(item => item.id === account.id)) {
          return currentAccounts;
        }

        return [...currentAccounts, account];
      }

      return currentAccounts.filter(item => item.id !== account.id);
    });
  };

  const handleRemoveDivertedSample = (accountNumber: string) => {
    setSelectedDivertedAccounts(currentAccounts =>
      currentAccounts.filter(account => String(account.accountNumber) !== accountNumber),
    );
  };

  const subTableColumns = useMemo(() => {
    const columns = getSubTableColConfig(id, selectedTable.row, false, isJobApproved);

    if (isJobApproved) {
      return columns;
    }

    return columns.map(column => {
      if (column.id !== 'divert') {
        return column;
      }

      const divertColumn: ColumnDef<JobSubTableAccount> = {
        ...column,
        cell: ({ row }) => {
          const val = row.original.divert;

          if (val !== 'checkbox') {
            return val;
          }

          const isChecked = selectedDivertedAccounts.some(account => account.id === row.original.id);

          return (
            <Checkbox
              checked={isChecked}
              onCheckedChange={checked => handleToggleDivert(row.original, checked === true)}
            />
          );
        },
      };

      return divertColumn;
    });
  }, [id, selectedTable.row, isJobApproved, selectedDivertedAccounts]);

  return (
    <div className="flex flex-col gap-4">
      <DataTable
        columns={id === 'Electronic' ? jobDetailColElectronic : jobDetailColPaper}
        data={jobDetailsState}
        variant="bordered"
      />
      <div className="h-[24px]" />
      {selectedTable.column ? (
        <div>
          <DataTable
            columns={subTableColumns}
            data={filteredThruSearchData} // jobSubTableData
            variant="bordered"
            paginationPosition="bottom"
          />
          {selectedTable.row === 'ACCOUNTS' ? (
            <div className="flex justify-end items-center">
              <DivertedSamplesDialog
                selectedSamples={selectedDivertedAccounts.map(account => ({
                  accountName: account.accountName,
                  accountNumber: String(account.accountNumber),
                }))}
                onRemoveSample={handleRemoveDivertedSample}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default JobDetailsTab;
